import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Paperclip, Send, ChevronDown, Trash2, FolderOpen, Upload, Save } from 'lucide-react';
import FilePickerDrawer from './FilePickerDrawer';
import { API_BASE as API } from '../../config';

const MAX_BYTES = 8 * 1024 * 1024;
const AUTOSAVE_DEBOUNCE_MS = 4000;

/** Reads a File object into a base64 string (without the data:…;base64, prefix)
 *  using the FileReader API. */
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const out = String(r.result || '');
      const comma = out.indexOf(',');
      resolve(comma >= 0 ? out.slice(comma + 1) : out);
    };
    r.onerror = () => reject(new Error('Failed to read file'));
    r.readAsDataURL(file);
  });

const splitList = (s) => (s || '').split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

const formatSavedAgo = (ts) => {
  if (!ts) return '';
  const secs = Math.max(1, Math.floor((Date.now() - new Date(ts).getTime()) / 1000));
  if (secs < 60) return `Saved ${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `Saved ${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  return `Saved ${hrs}h ago`;
};

/** Compose & Send modal for a claim email. Lives next to NotesModal but
 *  opened on demand (clicking the adjuster/contact email or the "New email"
 *  button on the Emails tab). */
const EmailComposeModal = ({
  open, onClose, reportNo, lossType, initialTo = '', initialSubject = '', onSent,
  /* Resume an existing draft: when set, we hydrate the form from the
     draft row instead of the initialTo/initialSubject defaults. The
     draft_id rides along on Send so the backend can discard it. */
  initialDraft = null,
  /* Notify parent (NotesModal) so the Drafts tab can refresh its list
     without a re-fetch after each save / delete. */
  onDraftChange = null,
}) => {
  const [to, setTo] = useState(initialTo);
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]); // {name, key?, content_base64?, content_type?, size, source}
  const [sending, setSending] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [attachMenu, setAttachMenu] = useState(false);
  // Draft state — `draftId` is the server-assigned ID once we've saved at
  // least once. `lastSavedAt` drives the inline "Saved 3s ago" label.
  const [draftId, setDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // True when the resumed draft had a from-computer attachment we couldn't
  // restore (server only persists metadata). Shows a one-line nudge.
  const [reattachHint, setReattachHint] = useState(false);
  const skipNextAutoSaveRef = useRef(false);
  const autosaveTimerRef = useRef(null);
  const savedAgoTickRef = useRef(0);
  const [, forceTick] = useState(0); // re-render the "Saved Xs ago" label
  const browseInputRef = useRef(null);

  // Reset state every time the modal transitions to OPEN. Using useState's
  // lazy initializer alone wouldn't react to subsequent open/close cycles —
  // and we explicitly WANT the To field to refresh from `initialTo` because
  // the parent passes a different email depending on which link was clicked
  // (Adjuster vs Other Party vs "+ New Email" with no prefill).
  //
  // Two modes:
  //   - With `initialDraft`: hydrate from the saved draft row, including
  //     attachments metadata. Mark `skipNextAutoSave` so we don't immediately
  //     overwrite the same row with the same content.
  //   - Without: fresh compose with optional pre-fill from the email link.
  useEffect(() => {
    if (!open) return;
    setAttachMenu(false);
    setReattachHint(false);
    if (initialDraft) {
      setTo((initialDraft.to || []).join(', '));
      setCc((initialDraft.cc || []).join(', '));
      setBcc((initialDraft.bcc || []).join(', '));
      setSubject(initialDraft.subject || '');
      setBody(initialDraft.body_text || '');
      setShowCcBcc(Boolean((initialDraft.cc || []).length || (initialDraft.bcc || []).length));
      // Only "from-claim" attachments restore — uploaded-from-computer
      // files were never persisted to GridFS (see email_drafts.py docstring).
      const restored = (initialDraft.attachments || [])
        .filter((a) => a.key && !a.from_computer)
        .map((a) => ({ name: a.name, key: a.key, content_type: a.content_type, size: a.size, source: 'claim' }));
      setAttachments(restored);
      setReattachHint((initialDraft.attachments || []).some((a) => a.from_computer));
      setDraftId(initialDraft.draft_id || null);
      setLastSavedAt(initialDraft.updated_at || null);
      skipNextAutoSaveRef.current = true;
    } else {
      setTo(initialTo || '');
      setSubject(initialSubject || '');
      setCc('');
      setBcc('');
      setBody('');
      setAttachments([]);
      setShowCcBcc(false);
      setDraftId(null);
      setLastSavedAt(null);
      skipNextAutoSaveRef.current = true; // don't autosave a totally blank shell
    }
  }, [open, initialTo, initialSubject, initialDraft]);

  // Tick the "Saved Xs ago" label once per second while the modal is open.
  useEffect(() => {
    if (!open) return undefined;
    const id = setInterval(() => {
      savedAgoTickRef.current += 1;
      forceTick((n) => n + 1);
    }, 1000);
    return () => clearInterval(id);
  }, [open]);

  // Debounced auto-save: whenever any composed field changes, wait 4s then
  // PUT to /email-drafts. We skip the very first effect run after a
  // hydrate/reset (skipNextAutoSaveRef) so opening the modal doesn't
  // immediately create a junk draft.
  useEffect(() => {
    if (!open) return undefined;
    if (skipNextAutoSaveRef.current) {
      skipNextAutoSaveRef.current = false;
      return undefined;
    }
    // Skip if the user hasn't really started a message yet.
    const hasContent =
      to.trim() || cc.trim() || bcc.trim() || subject.trim() || body.trim() || attachments.length > 0;
    if (!hasContent) return undefined;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      void saveDraft({ silent: true });
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(autosaveTimerRef.current);
  }, [open, to, cc, bcc, subject, body, attachments]);

  if (!open) return null;

  const onPickFromClaim = (picked) => {
    // Filter out already-attached files (in case the picker drawer mis-fired).
    const existing = new Set(attachments.map((a) => a.key).filter(Boolean));
    const fresh = picked.filter((p) => !existing.has(p.key)).map((p) => ({ ...p, source: 'claim' }));
    setAttachments((prev) => [...prev, ...fresh]);
  };

  const onBrowseFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    for (const file of files) {
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name} exceeds 8 MB limit`);
        continue;
      }
      try {
        const base64 = await fileToBase64(file);
        setAttachments((prev) => [...prev, {
          name: file.name,
          content_base64: base64,
          content_type: file.type || 'application/octet-stream',
          size: file.size,
          source: 'browse',
        }]);
      } catch {
        toast.error(`Failed to read ${file.name}`);
      }
    }
  };

  const removeAttachment = (i) => {
    setAttachments((prev) => prev.filter((_, idx) => idx !== i));
  };

  const buildDraftPayload = () => ({
    draft_id: draftId || undefined,
    report_no: reportNo,
    loss_type: lossType || null,
    to: splitList(to),
    cc: splitList(cc),
    bcc: splitList(bcc),
    subject,
    body_text: body,
    body_html: '',
    // Persist metadata only. Browse-uploaded files are flagged so we can
    // hint the user to re-attach on resume.
    attachments: attachments.map((a) => ({
      name: a.name,
      key: a.key || null,
      content_type: a.content_type || null,
      size: a.size,
      from_computer: a.source === 'browse',
    })),
    reply_to_email_id: initialDraft?.reply_to_email_id || null,
  });

  const saveDraft = async ({ silent = false } = {}) => {
    if (!reportNo) return;
    setSavingDraft(true);
    try {
      const res = await axios.post(`${API}/email-drafts`, buildDraftPayload());
      setDraftId(res.data.draft_id);
      setLastSavedAt(res.data.updated_at);
      if (!silent) toast.success('Draft saved');
      onDraftChange?.();
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const send = async () => {
    if (!to.trim()) { toast.error('Add at least one recipient'); return; }
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    setSending(true);
    try {
      const payload = {
        report_no: reportNo,
        to: splitList(to),
        cc: splitList(cc),
        bcc: splitList(bcc),
        subject: subject.trim(),
        body_text: body,
        loss_type: lossType || null,
        // Backend discards the draft row after a successful queue so the
        // Drafts tab clears itself without a manual cleanup.
        draft_id: draftId || null,
        // Thread the reply back into the original conversation so the
        // engagement pill, replied-by counts, and the thread modal align.
        reply_to_email_id: initialDraft?.reply_to_email_id || null,
        attachments: attachments.map((a) => ({
          name: a.name,
          key: a.key || null,
          content_base64: a.content_base64 || null,
          content_type: a.content_type || null,
          size: a.size,
        })),
      };
      const res = await axios.post(
        `${API}/claim-emails`,
        payload,
      );
      toast.success('Email sent');
      onSent?.(res.data);
      onDraftChange?.(); // draft is now gone server-side
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const headerLabel = initialDraft ? 'Resume Draft' : 'New Email';
  const savedLabel = savingDraft
    ? 'Saving…'
    : (lastSavedAt ? formatSavedAgo(lastSavedAt) : '');

  return (
    <div className="fixed inset-0 z-[55] flex items-end md:items-center justify-center bg-black/50" data-testid="email-compose-modal">
      <div className="bg-white w-full md:w-[720px] md:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col" style={{ maxHeight: '92vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div className="flex items-baseline gap-3">
            <h3 className="text-base font-semibold text-gray-900">{headerLabel}</h3>
            {savedLabel && (
              <span className="text-[11px] text-gray-400" data-testid="email-saved-label">{savedLabel}</span>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100" data-testid="email-compose-close">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {reattachHint && (
          <div className="px-5 py-2 text-[12px] text-amber-700 bg-amber-50 border-b border-amber-200" data-testid="email-reattach-hint">
            Re-attach any computer files — uploads from your previous session weren&apos;t saved.
          </div>
        )}

        {/* Fields */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2.5">
          <div className="flex items-center border-b border-gray-100 py-1.5">
            <span className="w-12 text-xs font-medium text-gray-500">To</span>
            <input
              type="text"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="email@example.com, another@example.com"
              className="flex-1 text-sm bg-transparent border-none focus:outline-none px-2"
              data-testid="email-to"
            />
            <button
              type="button"
              onClick={() => setShowCcBcc((v) => !v)}
              className="text-[11px] uppercase tracking-wide text-gray-400 hover:text-gray-600 px-2"
              data-testid="email-toggle-ccbcc"
            >
              Cc/Bcc
            </button>
          </div>

          {showCcBcc && (
            <>
              <div className="flex items-center border-b border-gray-100 py-1.5">
                <span className="w-12 text-xs font-medium text-gray-500">Cc</span>
                <input type="text" value={cc} onChange={(e) => setCc(e.target.value)} className="flex-1 text-sm bg-transparent border-none focus:outline-none px-2" data-testid="email-cc" />
              </div>
              <div className="flex items-center border-b border-gray-100 py-1.5">
                <span className="w-12 text-xs font-medium text-gray-500">Bcc</span>
                <input type="text" value={bcc} onChange={(e) => setBcc(e.target.value)} className="flex-1 text-sm bg-transparent border-none focus:outline-none px-2" data-testid="email-bcc" />
              </div>
            </>
          )}

          <div className="flex items-center border-b border-gray-100 py-1.5">
            <span className="w-12 text-xs font-medium text-gray-500">Subject</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={`Re: Claim ${reportNo}`}
              className="flex-1 text-sm bg-transparent border-none focus:outline-none px-2"
              data-testid="email-subject"
            />
          </div>

          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your message…"
            rows={10}
            className="w-full text-sm bg-transparent border-none focus:outline-none px-2 py-2 resize-y"
            data-testid="email-body"
          />

          {/* Attachments list */}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
              {attachments.map((a, i) => (
                <div key={`${a.key || a.name}-${i}`} className="inline-flex items-center gap-2 bg-gray-100 rounded-full pl-2.5 pr-1.5 py-1 text-xs" data-testid={`email-att-${a.name}`}>
                  <Paperclip className="w-3 h-3 text-gray-500" />
                  <span className="max-w-[160px] truncate" title={a.name}>{a.name}</span>
                  {a.size && <span className="text-[10px] text-gray-400">{(a.size / 1024).toFixed(0)} KB</span>}
                  <button onClick={() => removeAttachment(i)} className="p-0.5 rounded-full hover:bg-gray-200" title="Remove">
                    <Trash2 className="w-3 h-3 text-gray-500" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between gap-2">
          <div className="relative">
            <button
              onClick={() => setAttachMenu((v) => !v)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
              data-testid="email-attach-btn"
            >
              <Paperclip className="w-4 h-4" />
              Attach Files
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {attachMenu && (
              <div className="absolute bottom-full left-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden min-w-[160px] z-10">
                <button
                  onClick={() => { setAttachMenu(false); setPickerOpen(true); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  data-testid="email-attach-from-claim"
                >
                  <FolderOpen className="w-4 h-4 text-teal-600" /> From Claim
                </button>
                <button
                  onClick={() => { setAttachMenu(false); browseInputRef.current?.click(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  data-testid="email-attach-browse"
                >
                  <Upload className="w-4 h-4 text-blue-600" /> Browse…
                </button>
              </div>
            )}
            <input
              ref={browseInputRef}
              type="file"
              multiple
              onChange={onBrowseFiles}
              className="hidden"
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => saveDraft()}
              disabled={savingDraft}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 text-gray-700"
              data-testid="email-save-draft-btn"
            >
              <Save className="w-4 h-4" />
              {savingDraft ? 'Saving…' : 'Save Draft'}
            </button>
            <button
              onClick={send}
              disabled={sending}
              className="inline-flex items-center gap-1.5 px-5 py-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-sm font-medium rounded transition-colors"
              data-testid="email-send-btn"
            >
              <Send className="w-4 h-4" />
              {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      </div>

      <FilePickerDrawer
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        reportNo={reportNo}
        onPick={onPickFromClaim}
        alreadyPickedKeys={attachments.map((a) => a.key).filter(Boolean)}
      />
    </div>
  );
};

export default EmailComposeModal;
